"use client"

import { useState, useMemo } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { ChevronDown, ArrowUpDown, Search, BarChart3 } from "lucide-react"

const formatNumber = (n) => new Intl.NumberFormat("en-US").format(n || 0)

const ProgressBar = ({ value, tone = "primary" }) => (
  <div className="flex items-center gap-2 min-w-[90px]">
    <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
      <div
        className={`h-full rounded-full transition-all ${
          tone === "primary"
            ? "bg-primary"
            : value >= 70
            ? "bg-success"
            : value >= 40
            ? "bg-warning"
            : "bg-destructive"
        }`}
        style={{ width: `${Math.min(value, 100)}%` }}
      />
    </div>
    <span className="text-sm font-medium text-foreground tabular-nums">{value}%</span>
  </div>
)

const RowActions = ({ row, onViewAnalytics }) => {
  const [open, setOpen] = useState(false)

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground px-2 py-1 rounded-md hover:bg-muted transition-colors"
      >
        Actions
        <ChevronDown size={14} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-1 w-44 bg-card border border-border rounded-lg shadow-md z-20 py-1">
            <button
              onClick={() => {
                setOpen(false)
                onViewAnalytics?.(row)
              }}
              className="w-full flex items-center gap-2 text-left px-3 py-2 text-sm text-foreground hover:bg-muted transition-colors"
            >
              <BarChart3 size={14} /> View intelligence
            </button>
          </div>
        </>
      )}
    </div>
  )
}

const SORT_FIELDS = [
  { key: "name", label: "Program" },
  { key: "learners", label: "Learners" },
  { key: "progress", label: "Progress" },
  { key: "completion", label: "Completion" },
  { key: "credentials", label: "Credentials" },
]

export function ProgramPerformanceTable({ title = "Program Performance", programs = [], onViewAnalytics }) {
  const [search, setSearch] = useState("")
  const [sortField, setSortField] = useState("learners")
  const [sortOrder, setSortOrder] = useState("desc")

  const filtered = useMemo(() => {
    let rows = programs || []
    if (search?.trim()) {
      const q = search.trim().toLowerCase()
      rows = rows?.filter((p) => p?.name?.toLowerCase()?.includes(q))
    }
    return [...rows]?.sort((a, b) => {
      const aVal = a?.[sortField]
      const bVal = b?.[sortField]
      if (typeof aVal === "string") {
        return sortOrder === "asc" ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal)
      }
      return sortOrder === "asc" ? aVal - bVal : bVal - aVal
    })
  }, [programs, search, sortField, sortOrder])

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder((o) => (o === "asc" ? "desc" : "asc"))
    } else {
      setSortField(field)
      setSortOrder("desc")
    }
  }

  return (
    <div className="w-full">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <h3 className="text-lg font-semibold text-foreground">{title}</h3>

        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search programs..."
            className="pl-8 pr-3 py-1.5 text-sm bg-background border border-border rounded-md w-40 sm:w-52 focus:outline-none focus:ring-1 focus:ring-ring"
          />
        </div>
      </div>

      {filtered?.length === 0 ? (
        <div className="text-center py-12 text-sm text-muted-foreground bg-muted/30 rounded-lg border border-border">
          No programs yet. Programs you create will show up here with real learner data.
        </div>
      ) : (
        <>
          {/* Desktop / tablet: full table */}
          <div className="hidden md:block bg-card border border-border rounded-lg overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/30 text-xs font-medium text-muted-foreground">
                  {SORT_FIELDS?.map((field) => (
                    <th key={field?.key} className="text-left px-4 py-3">
                      <button onClick={() => handleSort(field?.key)} className="flex items-center gap-1 hover:text-foreground transition-colors">
                        {field?.label}
                        <ArrowUpDown size={12} className={sortField === field?.key ? "text-primary" : "opacity-40"} />
                      </button>
                    </th>
                  ))}
                  <th className="px-4 py-3 w-24" />
                </tr>
              </thead>
              <AnimatePresence mode="wait">
                <motion.tbody
                  key={`${search}-${sortField}-${sortOrder}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.15 }}
                >
                  {filtered?.map((row) => (
                    <tr key={row?.id} className="border-b border-border last:border-0 hover:bg-muted/20 transition-colors">
                      <td className="px-4 py-3">
                        <span className="text-sm font-medium text-foreground">{row?.name}</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-foreground tabular-nums">{formatNumber(row?.learners)}</td>
                      <td className="px-4 py-3">
                        <ProgressBar value={row?.progress} tone="primary" />
                      </td>
                      <td className="px-4 py-3">
                        <ProgressBar value={row?.completion} tone="status" />
                      </td>
                      <td className="px-4 py-3 text-sm font-semibold text-foreground tabular-nums">{formatNumber(row?.credentials)}</td>
                      <td className="px-4 py-3 text-right">
                        <RowActions row={row} onViewAnalytics={onViewAnalytics} />
                      </td>
                    </tr>
                  ))}
                </motion.tbody>
              </AnimatePresence>
            </table>
          </div>

          {/* Mobile: stacked cards */}
          <div className="md:hidden space-y-3">
            {filtered?.map((row) => (
              <div key={row?.id} className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-start justify-between gap-2 mb-3">
                  <p className="text-sm font-semibold text-foreground">{row?.name}</p>
                  <RowActions row={row} onViewAnalytics={onViewAnalytics} />
                </div>
                <div className="grid grid-cols-2 gap-3 text-xs mb-2">
                  <div>
                    <p className="text-muted-foreground mb-0.5">Learners</p>
                    <p className="font-medium text-foreground tabular-nums">{formatNumber(row?.learners)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground mb-0.5">Credentials</p>
                    <p className="font-semibold text-foreground tabular-nums">{formatNumber(row?.credentials)}</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <div>
                    <p className="text-muted-foreground text-xs mb-1">Progress</p>
                    <ProgressBar value={row?.progress} tone="primary" />
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs mb-1">Completion</p>
                    <ProgressBar value={row?.completion} tone="status" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

export default ProgramPerformanceTable